import { zodResolver } from '@hookform/resolvers/zod'
import { cn, getAuthClient, PASSWORT_MIN_LAENGE } from '@xhubio-saas/core-frontend/kernel'
import { Button, Input, Label } from '@xhubio-saas/core-frontend/ui-components'
import type { TFunction } from 'i18next'
import { Eye, EyeOff, Info, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { trpc } from '@/trpc'
import { SettingsLayout } from '../../../layouts/SettingsLayout'
import { CONTROL_TRANSITION, FIELD_RING } from '../../auth/authUi'

// Die drei Meldungen in der Sprache der Anwenderin (Issue CORE 2026-10-05): vorher standen hier feste englische Texte.
// Satz und Zahl der Mindestlaenge kommen aus dem Kernel, die beiden anderen Saetze aus der App (eigene Schluessel wie im Fork
// pdf-api-FE: `auth.validation.passwordsNoMatch` fuehren App und Kernel mit verschiedenen Texten).
function passwordSchema(t: TFunction) {
  return z
    .object({
      currentPassword: z.string().min(1, t('settings.currentPasswordRequired')),
      newPassword: z
        .string()
        .min(PASSWORT_MIN_LAENGE, t('auth.validation.passwordMin', { min: PASSWORT_MIN_LAENGE })),
      confirmPassword: z.string(),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('settings.passwordsDoNotMatch'),
      path: ['confirmPassword'],
    })
}

type PasswordFormData = z.infer<ReturnType<typeof passwordSchema>>

/**
 * Settings password page
 */
export function SettingsPasswordPage() {
  const { t } = useTranslation()
  const schema = useMemo(() => passwordSchema(t), [t])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  // Check if user has a social provider (Google, GitHub, Microsoft, Apple)
  const { data: profileInfo, isLoading: isLoadingProfile } = trpc.user.getProfile.useQuery(
    undefined,
    { retry: false }
  )
  const hasSocialProvider = profileInfo?.hasSocialProvider ?? false

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: PasswordFormData) => {
    setError(null)
    setSuccess(false)

    try {
      const authClient = getAuthClient()
      // better-auth client returns { data, error } instead of throwing
      const result = await authClient.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })
      if (result?.error) {
        const message = result.error.message || result.error.statusText
        setError(message || t('settings.passwordChangeFailed', 'Password change failed'))
        return
      }
      setSuccess(true)
      reset()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('settings.passwordChangeFailed', 'Password change failed')
      )
    }
  }

  // Show loading state
  if (isLoadingProfile) {
    return (
      <SettingsLayout>
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold">{t('settings.passwordTitle', 'Password')}</h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'settings.passwordDescription',
                'Update your password to keep your account secure.'
              )}
            </p>
          </div>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </SettingsLayout>
    )
  }

  // Show message for social provider users
  if (hasSocialProvider) {
    return (
      <SettingsLayout>
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold">{t('settings.passwordTitle', 'Password')}</h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'settings.passwordDescription',
                'Update your password to keep your account secure.'
              )}
            </p>
          </div>

          <div className="rounded-lg border border-muted bg-muted/50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                <Info className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <h3 className="font-medium">
                  {t('settings.passwordNotAvailable', 'Password management not available')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'settings.passwordSocialProviderInfo',
                    'You signed in using a social login provider (Google, GitHub, Microsoft, or Apple). Password management is handled by your provider.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </SettingsLayout>
    )
  }

  return (
    <SettingsLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold">{t('settings.passwordTitle', 'Password')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('settings.passwordDescription', 'Update your password to keep your account secure.')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4"
          data-testid="settings-password-form"
        >
          {error && (
            <div
              className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
              data-testid="settings-password-error"
            >
              {error}
            </div>
          )}

          {success && (
            <div
              className="rounded-md bg-success/10 p-3 text-sm text-success"
              data-testid="settings-password-success"
            >
              {t('settings.passwordChangeSuccess', 'Password changed successfully')}
            </div>
          )}

          {/* Fields are the shared ui Input (the raw <input>s were a second
              form system next to the auth screens). The eye toggles carry
              type="button" — without it a <button> inside a form defaults to
              submit, so tapping the eye used to submit the whole form. */}
          <div className="space-y-2">
            <Label htmlFor="currentPassword">
              {t('settings.currentPassword', 'Current password')}
            </Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                autoComplete="current-password"
                data-testid="settings-password-current"
                {...register('currentPassword')}
                aria-invalid={!!errors.currentPassword}
                aria-describedby={errors.currentPassword ? 'current-password-error' : undefined}
                className={cn('pr-10', FIELD_RING, errors.currentPassword && 'border-destructive')}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword((v) => !v)}
                className={cn(
                  'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-md text-muted-foreground hover:text-foreground',
                  CONTROL_TRANSITION,
                  FIELD_RING
                )}
                aria-label={
                  showCurrentPassword
                    ? t('auth.hidePassword', 'Hide password')
                    : t('auth.showPassword', 'Show password')
                }
                aria-pressed={showCurrentPassword}
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.currentPassword && (
              <p id="current-password-error" className="text-sm text-destructive">
                {errors.currentPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">{t('settings.newPassword', 'New password')}</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                data-testid="settings-password-new"
                {...register('newPassword')}
                aria-invalid={!!errors.newPassword}
                aria-describedby={errors.newPassword ? 'new-password-error' : undefined}
                className={cn('pr-10', FIELD_RING, errors.newPassword && 'border-destructive')}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                className={cn(
                  'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-md text-muted-foreground hover:text-foreground',
                  CONTROL_TRANSITION,
                  FIELD_RING
                )}
                aria-label={
                  showNewPassword
                    ? t('auth.hidePassword', 'Hide password')
                    : t('auth.showPassword', 'Show password')
                }
                aria-pressed={showNewPassword}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.newPassword && (
              <p id="new-password-error" className="text-sm text-destructive">
                {errors.newPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">
              {t('settings.confirmNewPassword', 'Confirm new password')}
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              data-testid="settings-password-confirm"
              {...register('confirmPassword')}
              aria-invalid={!!errors.confirmPassword}
              aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
              className={cn(FIELD_RING, errors.confirmPassword && 'border-destructive')}
            />
            {errors.confirmPassword && (
              <p id="confirm-password-error" className="text-sm text-destructive">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="settings-password-submit"
            className={cn(
              'gap-2',
              'transition-[background-color,transform] duration-[var(--dur-micro)] ease-[var(--ease-out)]',
              'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {isSubmitting && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
            {isSubmitting
              ? t('common.saving', 'Saving…')
              : t('settings.changePassword', 'Change password')}
          </Button>
        </form>
      </div>
    </SettingsLayout>
  )
}
